import {AsyncPage, Empty} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as O from 'fp-ts/lib/Option';
import {Dispatch, SetStateAction} from 'react';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import {ShoppingCartQuantities} from 'Shared/constants/ShoppingCart';
import {createAction} from 'Shared/utilities/actionCreator';
import {DateRange} from '../../../../Core/models/DateRange';
import {MarketingPreviousOrderDTO} from '../../../../Documents/api';
import {
  CartItemVisibility,
  DEFAULT_FORM_ID,
  DEFAULT_FORM_NUMBER,
  EmptyMarketingViewCart,
  MarketingViewCart,
} from '../../Marketing/ducks/Marketing';
import {Marketing} from '../../Marketing/models/Marketing';
import {PreviousOrder} from '../models/previousOrders';

export enum PreviousOrdersActionType {
  GET_PREVIOUS_ORDERS = 'previousOrders/GET_PREVIOUS_ORDERS',
  CHANGE_PREVIOUS_ORDERS = 'previousOrders/CHANGE_PREVIOUS_ORDERS',
  CHANGE_PREVIOUS_ORDERS_FILTER = 'previousOrders/CHANGE_PREVIOUS_ORDERS_FILTER',
  CHANGE_SORT = 'previousOrders/CHANGE_SORT',
  CLEAR_SORT = 'previousOrders/CLEAR_SORT',
  CLEAR_PREVIOUS_ORDERS = 'previousOrders/CLEAR',
}

export type PreviousOrdersFilters = {
  orderDate: DateRange;
  orderNumber: string;
  formNumber: string;
  formId: string;
  product: string;
  quantity: string;
  pageNumber: string;
  pageSize: string;
};

export const EmptyPreviousOrdersFilters: PreviousOrdersFilters = {
  orderDate: {
    endDate: '',
    startDate: '',
  },
  orderNumber: '',
  formNumber: '',
  formId: '',
  product: '',
  quantity: '',
  pageNumber: '1',
  pageSize: '25',
};

export const changePreviousOrdersFilters = (filter: PreviousOrdersFilters) =>
  createAction(PreviousOrdersActionType.CHANGE_PREVIOUS_ORDERS_FILTER, {filter});
export const changePreviousOrders = (data: AsyncPage<Page<PreviousOrder>>) =>
  createAction(PreviousOrdersActionType.CHANGE_PREVIOUS_ORDERS, data);
export const changeSort = (sort?: Sort) => createAction(PreviousOrdersActionType.CHANGE_SORT, {sort});
export const clearSort = () => createAction(PreviousOrdersActionType.CLEAR_SORT);
export const clearPreviousOrders = (filter: PreviousOrdersFilters) =>
  createAction(PreviousOrdersActionType.CLEAR_PREVIOUS_ORDERS, {filter});
export const getPreviousOrders = (
  page?: Page<MarketingPreviousOrderDTO>,
  filter?: PreviousOrdersFilters,
  sort?: Sort,
  isNewYork?: boolean
) => createAction(PreviousOrdersActionType.GET_PREVIOUS_ORDERS, {page, filter, sort, isNewYork});

type PreviousOrdersState = {
  previousOrders: AsyncPage<Page<PreviousOrder>>;
  filter: PreviousOrdersFilters;
  sort: O.Option<Sort>;
  cart: MarketingViewCart;
  marketing: AsyncPage<Page<Marketing>>;
};

export const DEFAULT_STATE: PreviousOrdersState = {
  previousOrders: new Empty(),
  filter: EmptyPreviousOrdersFilters,
  sort: O.none,
  cart: EmptyMarketingViewCart,
  marketing: new Empty(),
};

type PreviousOrdersAction =
  | ReturnType<typeof getPreviousOrders>
  | ReturnType<typeof clearSort>
  | ReturnType<typeof clearPreviousOrders>
  | ReturnType<typeof changeSort>
  | ReturnType<typeof changePreviousOrders>
  | ReturnType<typeof changePreviousOrdersFilters>;

export default (state: PreviousOrdersState , action: PreviousOrdersAction): PreviousOrdersState => {
  const currentState = state || DEFAULT_STATE;
  switch (action.type) {
    case PreviousOrdersActionType.CHANGE_PREVIOUS_ORDERS:
      return {
        ...currentState,
        previousOrders: action.payload,
      };
    case PreviousOrdersActionType.CHANGE_PREVIOUS_ORDERS_FILTER:
      action.payload.filter.pageNumber = '1';
      return {
        ...currentState,
        filter: action.payload.filter,
      };
    case PreviousOrdersActionType.CLEAR_SORT:
      state.filter.pageNumber = '1';

      return {
        ...currentState,
        sort: DEFAULT_STATE.sort,
      };
    case PreviousOrdersActionType.CHANGE_SORT:
      state.filter.pageNumber = '1';
      return {
        ...currentState,
        sort: O.fromNullable(action.payload.sort),
      };
    case PreviousOrdersActionType.CLEAR_PREVIOUS_ORDERS:
      state.filter = EmptyPreviousOrdersFilters;
      return {
        ...currentState,
        filter: action.payload.filter,
      };
    default:
      return currentState;
  }
};

const incrementedAmount = (value: any, currentFormIncrement: number, maxQuantity: number, minQuantity: number) => {
  if (value > maxQuantity) {
    return maxQuantity;
  }
  if (value < minQuantity) {
    return minQuantity;
  }
  if (value % currentFormIncrement > 0) {
    return Math.floor(value / currentFormIncrement) * currentFormIncrement + currentFormIncrement;
  } else {
    return value;
  }
};

export function onClickAddToCart(
  data: MarketingPreviousOrderDTO,
  visible: CartItemVisibility,
  cart: MarketingViewCart,
  setCartFinal: Dispatch<SetStateAction<MarketingViewCart>>,
  setVisible: Dispatch<SetStateAction<CartItemVisibility>>
) {
  const currentFormNumber = data && data.formNumber ? data.formNumber : DEFAULT_FORM_NUMBER;
  const currentFormId = data && data.formId ? data.formId : DEFAULT_FORM_ID;
  const unitIncrement = data ? Number(data.unitIncrements) : ShoppingCartQuantities.Increment;
  const maxQuantity = data ? Number(data.maxQuantity) : ShoppingCartQuantities.Max;
  const minQuantity = data ? Number(data.minQuantity) : ShoppingCartQuantities.Min;

  const marketingItem: Marketing = {
    title: data.title,
    formNumber: data.formNumber,
    fId: Number(currentFormId),
    quantity: Number(incrementedAmount(data.quantity, unitIncrement, maxQuantity, minQuantity)),
    unitIncrements: data.unitIncrements,
    maxQuantity: data.maxQuantity,
    minQuantity: data.minQuantity,
  };
  visible.inCartFormNumber.push(currentFormNumber);
  cart.items.set(currentFormNumber, {...marketingItem});
  setVisible({...visible});
  setCartFinal({...cart});
}
